import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Webhook, Trash2, Pause, Play, Send, CheckCircle2, XCircle, Clock, Sparkles, Workflow, Zap, MessageSquare, Info, CalendarClock, Rocket, ExternalLink } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  listIntegrations,
  createWebhookIntegration,
  createGHLIntegration,
  createSlackIntegration,
  startClickFunnelsOAuth,
  verifyClickFunnelsOAuthConnection,
  startGoHighLevelOAuth,
  verifyGoHighLevelOAuthConnection,
  deleteIntegration,
  setIntegrationStatus,
  testIntegrationPush,
  getPushStatus,
  listAutomations,
  createAutomation,
  deleteAutomation,
  setAutomationActive,
  testAutomation,
  listGHLWorkflows,
  listAccountWorkspaces,
  updateAutomationCutoff,
} from "@/lib/integrations.functions";
import { ContactSupportDialog } from "@/components/contact-support-dialog";
import { LifeBuoy } from "lucide-react";

const TRIGGER_LABELS: Record<string, string> = {
  "lifecycle.free_trial": "Contact starts a free trial",
  "lifecycle.active_paid": "Contact becomes a paid customer",
  "lifecycle.upgraded_paid": "Contact upgrades (Platinum/annual)",
  "lifecycle.churned": "Contact churns / cancels",
  "lifecycle.second_tier_active": "Contact becomes a 2nd-tier affiliate",
};
const TRIGGER_OPTIONS = Object.keys(TRIGGER_LABELS);
const CF_OAUTH_ORIGIN = "https://superaffiliateos.com";
const CF_OAUTH_CALLBACK_URL = `${CF_OAUTH_ORIGIN}/api/public/clickfunnels/oauth/callback`;

export const Route = createFileRoute("/_authenticated/settings/integrations")({
  component: IntegrationsPage,
});

function IntegrationsPage() {
  const path = useRouterState({ select: (r) => r.location.pathname });
  const fetchList = useServerFn(listIntegrations);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["account-integrations"],
    queryFn: () => fetchList(),
  });

  const integrations = data?.integrations ?? [];
  const recent = data?.recent ?? {};
  const [addOpen, setAddOpen] = useState(false);

  if (path !== "/settings/integrations") {
    return <Outlet />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-medium">Integrations</h2>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            Connect CRMs, email tools, and custom endpoints. Super Affiliate OS pushes state
            transitions (new trial, upgrade, churn) to keep your stack in sync, no manual exports.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/settings/integrations/push-management">
            <Button variant="outline">Manage queue</Button>
          </Link>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Add integration
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : integrations.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
          <Webhook className="h-8 w-8 mx-auto text-muted-foreground" />
          <h3 className="mt-3 text-sm font-medium">No integrations yet</h3>
          <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
            Start with a custom webhook to verify the pipeline end-to-end. GoHighLevel,
            ClickFunnels, and Slack are coming next.
          </p>
          <Button className="mt-4" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Connect webhook
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {integrations.map((i: any) => (
            <IntegrationRow
              key={i.id}
              integ={i}
              recent={recent[i.id]}
              onChange={() => qc.invalidateQueries({ queryKey: ["account-integrations"] })}
            />
          ))}
        </div>
      )}

      <AddIntegrationDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={() => qc.invalidateQueries({ queryKey: ["account-integrations"] })}
      />
    </div>
  );
}

function IntegrationRow({
  integ,
  recent,
  onChange,
}: {
  integ: any;
  recent?: { succeeded: number; failed: number; pending: number; last_at: string | null };
  onChange: () => void;
}) {
  const fnDelete = useServerFn(deleteIntegration);
  const fnStatus = useServerFn(setIntegrationStatus);
  const fnTest = useServerFn(testIntegrationPush);
  const fnPushStatus = useServerFn(getPushStatus);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [automationsOpen, setAutomationsOpen] = useState(false);

  const del = useMutation({
    mutationFn: () => fnDelete({ data: { id: integ.id } }),
    onSuccess: () => {
      toast.success("Integration removed");
      onChange();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const toggle = useMutation({
    mutationFn: () =>
      fnStatus({ data: { id: integ.id, status: integ.status === "active" ? "paused" : "active" } }),
    onSuccess: () => onChange(),
  });

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { push_id } = await fnTest({ data: { id: integ.id } });
      toast.info("Test push queued, waiting for worker (≤60s)…");
      // Poll status up to 90s
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { push } = await fnPushStatus({ data: { push_id } });
        if (!push) continue;
        if (push.status === "succeeded") {
          setTestResult(`✓ Delivered (${push.attempt_count} attempt${push.attempt_count > 1 ? "s" : ""})`);
          toast.success("Test push delivered");
          onChange();
          break;
        }
        if (push.status === "failed") {
          setTestResult(`✗ Failed: ${push.error_message ?? "unknown"}`);
          toast.error(`Test failed: ${push.error_message ?? "unknown"}`);
          onChange();
          break;
        }
      }
      if (!testResult) {
        // worker hasn't picked it up yet
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="rounded-lg bg-muted p-2">
            {integ.integration_type === "gohighlevel" ? (
              <Sparkles className="h-5 w-5" />
            ) : integ.integration_type === "clickfunnels" ? (
              <Rocket className="h-5 w-5" />
            ) : integ.integration_type === "slack" ? (
              <MessageSquare className="h-5 w-5" />
            ) : (
              <Webhook className="h-5 w-5" />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium truncate">{integ.display_name}</h3>
              <Badge variant={integ.status === "active" ? "default" : "secondary"} className="text-[10px]">
                {integ.status}
              </Badge>
              <Badge variant="outline" className="text-[10px] uppercase">
                {integ.integration_type}
              </Badge>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Created {new Date(integ.created_at).toLocaleDateString()}
              {integ.last_successful_push_at && (
                <> · Last success {new Date(integ.last_successful_push_at).toLocaleString()}</>
              )}
            </p>
            {recent && (
              <div className="mt-2 flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-3 w-3" /> {recent.succeeded}
                </span>
                <span className="flex items-center gap-1 text-destructive">
                  <XCircle className="h-3 w-3" /> {recent.failed}
                </span>
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Clock className="h-3 w-3" /> {recent.pending}
                </span>
                <span className="text-muted-foreground">last 7 days</span>
              </div>
            )}
            {integ.last_error && (
              <p className="mt-2 text-xs text-destructive truncate">
                Last error: {integ.last_error}
              </p>
            )}
            {testResult && (
              <p className="mt-2 text-xs font-medium">{testResult}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="outline" onClick={() => setAutomationsOpen(true)}>
            <Zap className="h-3 w-3 mr-1" /> Automations
          </Button>
          <Button size="sm" variant="outline" onClick={runTest} disabled={testing}>
            <Send className="h-3 w-3 mr-1" /> {testing ? "Sending…" : "Test"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => toggle.mutate()}>
            {integ.status === "active" ? (
              <Pause className="h-3 w-3" />
            ) : (
              <Play className="h-3 w-3" />
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              if (confirm(`Remove "${integ.display_name}"? Queued pushes will be discarded.`)) {
                del.mutate();
              }
            }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
      <AutomationsDialog
        open={automationsOpen}
        onOpenChange={setAutomationsOpen}
        integration={integ}
      />
    </div>
  );
}

function AddIntegrationDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add integration</DialogTitle>
          <DialogDescription>
            Pipe lifecycle events (new trial, upgrade, churn) into the tools you already use.
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="ghl">
          <TabsList className="w-full">
            <TabsTrigger value="ghl" className="flex-1">
              <Sparkles className="h-4 w-4 mr-1" /> GoHighLevel
            </TabsTrigger>
            <TabsTrigger value="cf2" className="flex-1">
              <Rocket className="h-4 w-4 mr-1" /> ClickFunnels 2.0
            </TabsTrigger>
            <TabsTrigger value="slack" className="flex-1">
              <MessageSquare className="h-4 w-4 mr-1" /> Slack
            </TabsTrigger>
            <TabsTrigger value="webhook" className="flex-1">
              <Webhook className="h-4 w-4 mr-1" /> Webhook
            </TabsTrigger>
          </TabsList>
          <TabsContent value="ghl" className="mt-4">
            <Tabs defaultValue="oauth">
              <TabsList className="w-full">
                <TabsTrigger value="oauth" className="flex-1">OAuth (recommended)</TabsTrigger>
                <TabsTrigger value="pit" className="flex-1">Private Token</TabsTrigger>
              </TabsList>
              <TabsContent value="oauth" className="mt-4">
                <GHLOAuthForm onConnected={() => { onCreated(); onOpenChange(false); }} />
              </TabsContent>
              <TabsContent value="pit" className="mt-4">
                <GHLForm onCreated={() => { onCreated(); onOpenChange(false); }} />
              </TabsContent>
            </Tabs>
          </TabsContent>
          <TabsContent value="cf2" className="mt-4">
            <ClickFunnelsForm onConnected={() => { onCreated(); onOpenChange(false); }} />
          </TabsContent>
          <TabsContent value="slack" className="mt-4">
            <SlackForm onCreated={() => { onCreated(); onOpenChange(false); }} />
          </TabsContent>
          <TabsContent value="webhook" className="mt-4">
            <WebhookForm onCreated={() => { onCreated(); onOpenChange(false); }} />
          </TabsContent>
        </Tabs>
        <div className="mt-6 rounded-md border border-border bg-muted/30 p-4">
          <p className="text-sm font-medium text-foreground">Don't see something you need?</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Request an integration or get support — most platforms also work via the Webhook tab above (point it at your autoresponder's incoming-webhook URL or a Zapier hook).
          </p>
          <SupportTriggerButton defaultCategory="integration_request" />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SupportTriggerButton({ defaultCategory }: { defaultCategory?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" className="mt-3" onClick={() => setOpen(true)}>
        <LifeBuoy className="h-4 w-4 mr-1" /> Contact support
      </Button>
      <ContactSupportDialog open={open} onOpenChange={setOpen} defaultCategory={defaultCategory} />
    </>
  );
}

function GHLOAuthForm({ onConnected }: { onConnected: () => void }) {
  const fn = useServerFn(startGoHighLevelOAuth);
  const verifyConnection = useServerFn(verifyGoHighLevelOAuthConnection);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const isCanonicalDomain = typeof window !== "undefined" && window.location.origin === CF_OAUTH_ORIGIN;

  const connect = async () => {
    setBusy(true);
    try {
      const { authorize_url } = await fn({ data: { display_name: name } });
      const popup = window.open(authorize_url, "ghl-oauth", "width=600,height=750");
      let poll: ReturnType<typeof setInterval> | undefined;
      const confirmConnected = async (integrationId?: string) => {
        const result = await verifyConnection({ data: { integration_id: integrationId } });
        if (!result.connected) {
          toast.error("GoHighLevel authorization finished, but the integration was not saved. Please reconnect.");
          return;
        }
        toast.success("GoHighLevel connected");
        onConnected();
      };
      const onMessage = (e: MessageEvent) => {
        if (e?.data?.type === "ghl-connected") {
          window.removeEventListener("message", onMessage);
          if (poll) clearInterval(poll);
          void confirmConnected(e.data.integrationId);
        }
      };
      window.addEventListener("message", onMessage);
      poll = setInterval(() => {
        if (popup?.closed) {
          clearInterval(poll);
          window.removeEventListener("message", onMessage);
          void confirmConnected();
        }
      }, 1000);
    } catch (e: any) {
      toast.error(e?.message ?? "Could not start OAuth");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md bg-muted/40 border border-border p-3 text-xs text-muted-foreground space-y-2">
        <p>
          Click <strong>Connect</strong> below and authorize Super Affiliate OS in your GoHighLevel
          sub-account. We'll store the OAuth tokens encrypted at rest and auto-refresh them — no
          manual token rotation required.
        </p>
        {!isCanonicalDomain && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2.5 text-destructive">
            You're on a temporary editor URL. Open your published site (superaffiliateos.com) to connect GoHighLevel.
          </div>
        )}
      </div>
      <div className="space-y-1">
        <Label>Name</Label>
        <Input
          placeholder="e.g. GHL, Main agency"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <DialogFooter className="pt-2">
        <Button onClick={connect} disabled={!name || busy || !isCanonicalDomain}>
          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
          {busy ? "Opening…" : "Connect GoHighLevel"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function GHLForm({ onCreated }: { onCreated: () => void }) {
  const fn = useServerFn(createGHLIntegration);
  const [name, setName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [locationId, setLocationId] = useState("");

  const create = useMutation({
    mutationFn: () =>
      fn({ data: { display_name: name, api_key: apiKey, location_id: locationId } }),
    onSuccess: () => {
      toast.success("GoHighLevel connected");
      setName("");
      setApiKey("");
      setLocationId("");
      onCreated();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  return (
    <div className="space-y-3">
      <div className="rounded-md bg-muted/40 border border-border p-3 text-xs text-muted-foreground">
        Use a <strong>Private Integration Token</strong> from GHL: Settings → Private Integrations
        → <em>Create new</em>. Required scopes: <code>contacts.write</code>,{" "}
        <code>contacts.readonly</code>, and <code>workflows.readonly</code> (for workflow
        enrollment). Token is encrypted at rest; we never expose it back to the browser.
      </div>
      <div className="space-y-1">
        <Label>Name</Label>
        <Input
          placeholder="e.g. GHL, Production"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label>Location ID</Label>
        <Input
          placeholder="e.g. abc123XYZ"
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Found in GHL Settings → Business Profile, or in the URL of your sub-account.
        </p>
      </div>
      <div className="space-y-1">
        <Label>Private Integration Token</Label>
        <Input
          placeholder="pit-…"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          type="password"
        />
      </div>
      <DialogFooter className="pt-2">
        <Button
          onClick={() => create.mutate()}
          disabled={!name || !apiKey || !locationId || create.isPending}
        >
          {create.isPending ? "Connecting…" : "Connect GoHighLevel"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function ClickFunnelsForm({ onConnected }: { onConnected: () => void }) {
  const fn = useServerFn(startClickFunnelsOAuth);
  const verifyConnection = useServerFn(verifyClickFunnelsOAuthConnection);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const isCanonicalDomain = typeof window !== "undefined" && window.location.origin === CF_OAUTH_ORIGIN;

  const connect = async () => {
    setBusy(true);
    try {
      const { authorize_url } = await fn({ data: { display_name: name } });
      const popup = window.open(authorize_url, "cf2-oauth", "width=600,height=750");
      let poll: ReturnType<typeof setInterval> | undefined;
      const confirmConnected = async (integrationId?: string) => {
        const result = await verifyConnection({ data: { integration_id: integrationId } });
        if (!result.connected) {
          toast.error("ClickFunnels authorization finished, but the integration was not saved. Please reconnect.");
          return;
        }
        toast.success("ClickFunnels 2.0 connected");
        onConnected();
      };
      const onMessage = (e: MessageEvent) => {
        if (e?.data?.type === "cf2-connected") {
          window.removeEventListener("message", onMessage);
          if (poll) clearInterval(poll);
          void confirmConnected(e.data.integrationId);
        }
      };
      window.addEventListener("message", onMessage);
      // Fallback: the popup may close before postMessage arrives, but never mark success without verifying persistence.
      poll = setInterval(() => {
        if (popup?.closed) {
          clearInterval(poll);
          window.removeEventListener("message", onMessage);
          void confirmConnected();
        }
      }, 1000);
    } catch (e: any) {
      toast.error(e?.message ?? "Could not start OAuth");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md bg-muted/40 border border-border p-3 text-xs text-muted-foreground space-y-2">
        <p>
          Click <strong>Connect</strong> below and authorize Super Affiliate OS in your ClickFunnels 2.0
          workspace. We'll store the OAuth tokens encrypted at rest and auto-refresh them.
        </p>
        {!isCanonicalDomain && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2.5 text-destructive">
            You're on a temporary editor URL. Open your published site to connect ClickFunnels.
          </div>
        )}

        <div className="rounded-md border border-border bg-background p-2.5">
          <p className="font-medium text-foreground">
            Default lifecycle tags
          </p>
          <p className="mt-1">
            Out of the box, Super Affiliate OS adds and removes these tags on contacts as their
            lifecycle changes. Build CF2 workflows (welcome email, win-back, upsell) that trigger
            when the matching tag is applied:
          </p>
          <ul className="mt-2 space-y-0.5 font-mono text-[11px]">
            <li><code>ms-ai-trial</code> — contact started a free trial</li>
            <li><code>ms-ai-active</code> — contact became a paid customer</li>
            <li><code>ms-ai-platinum</code> — contact upgraded (Platinum / annual)</li>
            <li><code>ms-ai-churned</code> — contact churned or canceled</li>
          </ul>
          <p className="mt-2">
            We cross-reference by email — if a contact upgrades to Platinum, the
            <code className="mx-1">ms-ai-churned</code> tag is automatically removed so you never send a
            "why did you cancel" email to a paying customer.
          </p>
          <p className="mt-2">
            Prefer your own tag names? When you create an automation below you can type any tag —
            it'll be created in ClickFunnels automatically if it doesn't exist yet.
          </p>
        </div>

      </div>
      <div className="space-y-1">
        <Label>Name</Label>
        <Input
          placeholder="e.g. ClickFunnels, Main workspace"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <DialogFooter className="pt-2">
        <Button onClick={connect} disabled={!name || busy || !isCanonicalDomain}>
          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
          {busy ? "Opening…" : "Connect ClickFunnels 2.0"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function WebhookForm({ onCreated }: { onCreated: () => void }) {
  const fn = useServerFn(createWebhookIntegration);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");

  const create = useMutation({
    mutationFn: () => fn({ data: { display_name: name, url, secret: secret || undefined } }),
    onSuccess: () => {
      toast.success("Webhook connected");
      setName("");
      setUrl("");
      setSecret("");
      onCreated();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  return (
    <div className="space-y-3">
      <div className="rounded-md bg-muted/40 border border-border p-3 text-xs text-muted-foreground">
        We POST a signed JSON event to your URL on every transition. Test with{" "}
        <a href="https://webhook.site" target="_blank" rel="noreferrer" className="underline">
          webhook.site
        </a>
        .
      </div>
      <div className="space-y-1">
        <Label>Name</Label>
        <Input
          placeholder="e.g. Zapier, Lifecycle events"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label>URL</Label>
        <Input
          placeholder="https://hooks.example.com/abc123"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label>Signing secret (optional)</Label>
        <Input
          placeholder="Used to compute x-saos-signature"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          type="password"
        />
        <p className="text-xs text-muted-foreground">
          If set, we send <code className="text-[10px]">x-saos-signature: t=&lt;ts&gt;,v1=&lt;hex&gt;</code>{" "}
          where <code className="text-[10px]">v1 = HMAC-SHA256(secret, ts + "." + body)</code>.
        </p>
      </div>
      <DialogFooter className="pt-2">
        <Button onClick={() => create.mutate()} disabled={!name || !url || create.isPending}>
          {create.isPending ? "Connecting…" : "Connect webhook"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function SlackForm({ onCreated }: { onCreated: () => void }) {
  const fn = useServerFn(createSlackIntegration);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [channel, setChannel] = useState("");

  const create = useMutation({
    mutationFn: () =>
      fn({
        data: {
          display_name: name,
          webhook_url: url,
          default_channel: channel || undefined,
        },
      }),
    onSuccess: () => {
      toast.success("Slack connected");
      setName("");
      setUrl("");
      setChannel("");
      onCreated();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  return (
    <div className="space-y-3">
      <div className="rounded-md bg-muted/40 border border-border p-3 text-xs text-muted-foreground">
        Create an <strong>Incoming Webhook</strong> in Slack:{" "}
        <a
          href="https://api.slack.com/messaging/webhooks"
          target="_blank"
          rel="noreferrer"
          className="underline"
        >
          api.slack.com/messaging/webhooks
        </a>
        . Pick the channel you want lifecycle alerts posted to, install the app to your
        workspace, then paste the webhook URL below. We'll post a Block Kit message on
        every state transition.
      </div>
      <div className="space-y-1">
        <Label>Name</Label>
        <Input
          placeholder="e.g. Slack, #revenue-alerts"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label>Webhook URL</Label>
        <Input
          placeholder="https://hooks.slack.com/services/T…/B…/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          type="password"
        />
      </div>
      <div className="space-y-1">
        <Label>Override channel (optional)</Label>
        <Input
          placeholder="#another-channel"
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Leave blank to post to the channel the webhook was created for. Channel overrides
          only work on legacy webhooks; modern apps will ignore this.
        </p>
      </div>
      <DialogFooter className="pt-2">
        <Button onClick={() => create.mutate()} disabled={!name || !url || create.isPending}>
          {create.isPending ? "Connecting…" : "Connect Slack"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function AutomationsDialog({
  open,
  onOpenChange,
  integration,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  integration: any;
}) {
  const qc = useQueryClient();
  const fnList = useServerFn(listAutomations);
  const fnDelete = useServerFn(deleteAutomation);
  const fnToggle = useServerFn(setAutomationActive);
  const fnTestAuto = useServerFn(testAutomation);
  const fnPushStatus = useServerFn(getPushStatus);
  const fnUpdateCutoff = useServerFn(updateAutomationCutoff);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [editingCutoffId, setEditingCutoffId] = useState<string | null>(null);
  const [cutoffDraft, setCutoffDraft] = useState<string>("");

  const runAutomationTest = async (automationId: string) => {
    setTestingId(automationId);
    try {
      const { push_id, test_email } = await fnTestAuto({ data: { automation_id: automationId } });
      toast.info(`Test queued for ${test_email} (≤60s)…`);
      const deadline = Date.now() + 90_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const { push } = await fnPushStatus({ data: { push_id } });
        if (push?.status === "succeeded") {
          toast.success("Automation test delivered — check the workflow in GHL");
          return;
        }
        if (push?.status === "failed") {
          toast.error(`Automation test failed: ${push.error_message ?? "unknown"}`);
          return;
        }
      }
      toast.warning("Still pending after 90s — check Push history");
    } catch (e: any) {
      toast.error(e?.message ?? "Test failed");
    } finally {
      setTestingId(null);
    }
  };
  const [addOpen, setAddOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["automations", integration.id],
    queryFn: () => fnList({ data: { integration_id: integration.id } }),
    enabled: open,
  });

  const del = useMutation({
    mutationFn: (id: string) => fnDelete({ data: { id } }),
    onSuccess: () => {
      toast.success("Automation removed");
      refetch();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const toggle = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      fnToggle({ data: { id, is_active } }),
    onSuccess: () => refetch(),
  });

  const saveCutoff = useMutation({
    mutationFn: ({ id, min_transition_date }: { id: string; min_transition_date: string | null }) =>
      fnUpdateCutoff({ data: { id, min_transition_date } }),
    onSuccess: () => {
      toast.success("Cutoff updated");
      setEditingCutoffId(null);
      refetch();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const automations = data?.automations ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-4 w-4" /> Automations, {integration.display_name}
          </DialogTitle>
          <DialogDescription>
            When a contact's lifecycle changes in Super Affiliate OS, automatically enroll them
            in a workflow or update tags in {integration.integration_type === "gohighlevel" ? "GoHighLevel" : integration.integration_type === "clickfunnels" ? "ClickFunnels 2.0" : "your destination"}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {isLoading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : automations.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center">
              <Workflow className="h-6 w-6 mx-auto text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">No automations yet.</p>
              <p className="text-xs text-muted-foreground">
                Add one to start sending contacts into workflows on lifecycle events.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {automations.map((a: any) => (
                <div
                  key={a.id}
                  className="flex items-start justify-between gap-3 rounded-lg border border-border bg-card p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">
                        {a.workspace_name ?? "workspace"}
                      </Badge>
                      <span className="text-xs text-muted-foreground">when</span>
                      <span className="text-sm font-medium">
                        {TRIGGER_LABELS[a.trigger_event] ?? a.trigger_event}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      → {describeAction(a.action_type, a.action_config)}
                    </div>
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                      <CalendarClock className="h-3 w-3 text-muted-foreground" />
                      {editingCutoffId === a.id ? (
                        <>
                          <Input
                            type="date"
                            value={cutoffDraft}
                            onChange={(e) => setCutoffDraft(e.target.value)}
                            max={new Date().toISOString().slice(0, 10)}
                            className="h-7 w-36 text-xs"
                          />
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                            onClick={() => saveCutoff.mutate({ id: a.id, min_transition_date: cutoffDraft || null })}
                            disabled={saveCutoff.isPending}
                          >Save</Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                            onClick={() => saveCutoff.mutate({ id: a.id, min_transition_date: null })}
                          >Clear</Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                            onClick={() => setEditingCutoffId(null)}
                          >Cancel</Button>
                        </>
                      ) : (
                        <>
                          <span className="text-xs text-muted-foreground">
                            {a.action_config?.min_transition_date
                              ? `Only fires for changes after ${new Date(a.action_config.min_transition_date).toLocaleDateString()}`
                              : <span className="text-amber-600 dark:text-amber-500">No cutoff — first import will fire on full history</span>}
                          </span>

                          <button
                            type="button"
                            className="text-xs text-primary hover:underline"
                            onClick={() => {
                              const cur = a.action_config?.min_transition_date
                                ? new Date(a.action_config.min_transition_date).toISOString().slice(0, 10)
                                : new Date().toISOString().slice(0, 10);
                              setCutoffDraft(cur);
                              setEditingCutoffId(a.id);
                            }}
                          >Edit</button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={testingId === a.id || !a.is_active}
                      onClick={() => runAutomationTest(a.id)}
                      title={a.is_active ? "Send a synthetic contact through this automation" : "Activate the automation first"}
                    >
                      <Send className="h-3 w-3 mr-1" />
                      {testingId === a.id ? "Testing…" : "Test"}
                    </Button>
                    <Switch
                      checked={a.is_active}
                      onCheckedChange={(v) => toggle.mutate({ id: a.id, is_active: v })}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (confirm("Remove this automation?")) del.mutate(a.id);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Add automation
          </Button>
        </DialogFooter>

        <AddAutomationDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          integration={integration}
          onCreated={() => {
            refetch();
            qc.invalidateQueries({ queryKey: ["automations", integration.id] });
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

function describeAction(actionType: string, cfg: any): string {
  if (actionType === "enroll_workflow") {
    return `Enroll in workflow "${cfg?.workflow_name ?? cfg?.workflow_id ?? ","}"`;
  }
  if (actionType === "add_tag") {
    return `Add tag(s): ${(cfg?.tags ?? []).join(", ")}`;
  }
  if (actionType === "remove_tag") {
    return `Remove tag(s): ${(cfg?.tags ?? []).join(", ")}`;
  }
  return actionType;
}

function AddAutomationDialog({
  open,
  onOpenChange,
  integration,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  integration: any;
  onCreated: () => void;
}) {
  const fnCreate = useServerFn(createAutomation);
  const fnWorkspaces = useServerFn(listAccountWorkspaces);
  const fnWorkflows = useServerFn(listGHLWorkflows);

  const [workspaceId, setWorkspaceId] = useState<string>("");
  const [triggerEvent, setTriggerEvent] = useState<string>("lifecycle.churned");
  const [actionType, setActionType] = useState<string>(
    integration.integration_type === "gohighlevel" ? "enroll_workflow" : "add_tag",
  );
  const [workflowId, setWorkflowId] = useState<string>("");
  const [tagInput, setTagInput] = useState<string>("");
  const [minTransitionDate, setMinTransitionDate] = useState<string>(
    () => new Date().toISOString().slice(0, 10), // default to today
  );

  const { data: wsData } = useQuery({
    queryKey: ["account-workspaces"],
    queryFn: () => fnWorkspaces(),
    enabled: open,
  });
  const workspaces = wsData?.workspaces ?? [];

  const {
    data: wfData,
    isLoading: wfLoading,
    error: wfError,
    refetch: refetchWorkflows,
  } = useQuery({
    queryKey: ["ghl-workflows", integration.id],
    queryFn: () => fnWorkflows({ data: { integration_id: integration.id } }),
    enabled: open && integration.integration_type === "gohighlevel" && actionType === "enroll_workflow",
    retry: false,
  });
  const workflows = wfData?.workflows ?? [];

  const create = useMutation({
    mutationFn: () => {
      let action_config: Record<string, any> = {};
      if (actionType === "enroll_workflow") {
        const wf = workflows.find((w: any) => w.id === workflowId);
        action_config = { workflow_id: workflowId, workflow_name: wf?.name };
      } else {
        const tags = tagInput
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
        action_config = { tags };
      }
      return fnCreate({
        data: {
          integration_id: integration.id,
          workspace_id: workspaceId,
          trigger_event: triggerEvent as any,
          action_type: actionType as any,
          action_config,
          min_transition_date: minTransitionDate || null,
        },
      });
    },
    onSuccess: () => {
      toast.success("Automation created");
      setWorkflowId("");
      setTagInput("");
      onCreated();
      onOpenChange(false);
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const canSubmit =
    !!workspaceId &&
    !!triggerEvent &&
    !!actionType &&
    (actionType === "enroll_workflow"
      ? !!workflowId
      : tagInput.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New automation</DialogTitle>
          <DialogDescription>
            Pick the lifecycle event and what should happen in {integration.display_name}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Program (workspace)</Label>
            <Select value={workspaceId} onValueChange={setWorkspaceId}>
              <SelectTrigger><SelectValue placeholder="Choose a program…" /></SelectTrigger>
              <SelectContent>
                {workspaces.map((w: any) => (
                  <SelectItem key={w.id} value={w.id}>{w.program_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>When</Label>
            <Select value={triggerEvent} onValueChange={setTriggerEvent}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TRIGGER_OPTIONS.map((t) => (
                  <SelectItem key={t} value={t}>{TRIGGER_LABELS[t]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Then</Label>
            <Select value={actionType} onValueChange={setActionType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {integration.integration_type === "gohighlevel" && (
                  <SelectItem value="enroll_workflow">Enroll in workflow</SelectItem>
                )}
                <SelectItem value="add_tag">Add tag(s)</SelectItem>
                <SelectItem value="remove_tag">Remove tag(s)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {actionType === "enroll_workflow" ? (
            <div className="space-y-1">
              <Label>Workflow</Label>
              {wfLoading ? (
                <div className="text-xs text-muted-foreground">Loading workflows from GoHighLevel…</div>
              ) : wfError ? (
                <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-xs text-destructive">
                  {(wfError as any)?.message ?? "Failed to load workflows."}
                  <Button size="sm" variant="ghost" className="ml-2 h-6" onClick={() => refetchWorkflows()}>Retry</Button>
                </div>
              ) : workflows.length === 0 ? (
                <div className="text-xs text-muted-foreground">
                  No workflows found in this location. Create one in GoHighLevel first.
                </div>
              ) : (
                <Select value={workflowId} onValueChange={setWorkflowId}>
                  <SelectTrigger><SelectValue placeholder="Choose a workflow…" /></SelectTrigger>
                  <SelectContent>
                    {workflows.map((w: any) => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.name}{w.status ? ` · ${w.status}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-muted-foreground">
                Requires <code>workflows.readonly</code> on your Private Integration Token.
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              <Label>Tag(s)</Label>
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="e.g. churned, winback (comma separated)"
              />
            </div>
          )}

          <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-3">
            <div className="flex items-center gap-1.5">
              <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
              <Label className="text-sm">Only fire for changes detected after</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className="text-muted-foreground hover:text-foreground" aria-label="What is this?">
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent side="top" className="w-80 text-xs leading-relaxed">
                  <p className="font-medium text-foreground mb-1.5">Why this matters</p>
                  <p className="text-muted-foreground mb-2">
                    When you upload a CSV, Super Affiliate OS scans the whole file and detects every lifecycle
                    change (new trial, churn, upgrade). Without a cutoff, this automation would fire for{" "}
                    <span className="font-medium text-foreground">every</span> matching change — including ones
                    from months or years ago.
                  </p>
                  <p className="font-medium text-foreground mb-1.5">Example</p>
                  <p className="text-muted-foreground">
                    A contact churned 8 months ago and already got your win-back email back then. If you import
                    a full-history CSV today with no cutoff, the automation would re-enroll them and they'd get
                    the same email again. Set the cutoff to today and only newly detected changes go through.
                  </p>
                </PopoverContent>
              </Popover>
            </div>
            <Input
              type="date"
              value={minTransitionDate}
              onChange={(e) => setMinTransitionDate(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
            />
            <p className="text-xs text-muted-foreground">
              {minTransitionDate
                ? "Lifecycle changes detected before this date will be skipped."
                : "No cutoff — your first import will fire for every historical change. Subsequent uploads only fire for new changes since the previous upload."}
            </p>

          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => create.mutate()} disabled={!canSubmit || create.isPending}>
            {create.isPending ? "Saving…" : "Create automation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
